import { FC, useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Flex,
	Spin,
	Button,
	Modal,
	Form,
	Input,
	Select,
	DatePicker,
	Popconfirm,
	message,
	Typography,
	Tag,
	Divider,
	Empty,
	Switch
} from 'antd'
import { PlusOutlined, DeleteOutlined, WifiOutlined } from '@ant-design/icons'
import {
	DeviceInstallation,
	MountType,
	RiskLevel
} from '../helpers/types/inventory'
import {
	fetchDeviceInstallations,
	createDeviceInstallation,
	deleteDeviceInstallation
} from '../api/inventory'

const { Text, Title } = Typography

// ─── Risk level display ───────────────────────────────────────────────────────
const RISK_COLOR: Record<RiskLevel, string> = {
	low: '#52c41a',
	medium: '#faad14',
	high: '#ff4d4f'
}

const RISK_EMOJI: Record<RiskLevel, string> = {
	low: '🟢',
	medium: '🟡',
	high: '🔴'
}

const RiskCell: FC<{ level: RiskLevel | null }> = ({ level }) => {
	if (!level) return <Text type="secondary">—</Text>
	return (
		<span
			style={{
				color: RISK_COLOR[level],
				fontWeight: 600,
				fontSize: 13
			}}>
			{RISK_EMOJI[level]} {level.toUpperCase()}
		</span>
	)
}

// ─── Risk matrix table ────────────────────────────────────────────────────────
const RiskMatrix: FC<{ install: DeviceInstallation }> = ({ install }) => {
	const risks: { label: string; value: RiskLevel | null }[] = [
		{ label: 'Vibration', value: install.vibrationRisk },
		{ label: 'Dust', value: install.dustRisk },
		{ label: 'Water', value: install.waterRisk },
		{ label: 'Heat', value: install.heatRisk }
	]

	return (
		<table
			style={{
				borderCollapse: 'collapse',
				fontSize: 12,
				marginTop: 4
			}}>
			<thead>
				<tr>
					<th
						style={{
							textAlign: 'left',
							padding: '4px 12px 4px 0',
							color: '#8492a6',
							fontWeight: 500
						}}>
						Risk
					</th>
					<th
						style={{
							padding: '4px 12px',
							color: '#8492a6',
							fontWeight: 500
						}}>
						Level
					</th>
				</tr>
			</thead>
			<tbody>
				{risks.map(({ label, value }) => (
					<tr key={label}>
						<td
							style={{
								padding: '3px 12px 3px 0',
								color: '#374151'
							}}>
							{label}
						</td>
						<td style={{ padding: '3px 12px' }}>
							<RiskCell level={value} />
						</td>
					</tr>
				))}
			</tbody>
		</table>
	)
}

// ─── Add Device Installation modal ───────────────────────────────────────────
interface AddInstallModalProps {
	carrierId: number
	open: boolean
	onClose: () => void
	onCreated: (d: DeviceInstallation) => void
}

const RISK_OPTIONS = ['low', 'medium', 'high'] as RiskLevel[]
const MOUNT_OPTIONS = ['bolt', 'rail', 'adhesive'] as MountType[]

const AddInstallModal: FC<AddInstallModalProps> = ({
	carrierId,
	open,
	onClose,
	onCreated
}) => {
	const { t } = useTranslation()
	const [form] = Form.useForm()
	const [saving, setSaving] = useState(false)

	const handleOk = async () => {
		const values = await form.validateFields()
		setSaving(true)
		try {
			const d = await createDeviceInstallation({
				carrierId,
				...values,
				installDate: values.installDate
					? values.installDate.format('YYYY-MM-DD')
					: undefined
			})
			onCreated(d)
			form.resetFields()
			onClose()
		} catch {
			message.error(t('Error'))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Modal
			title="Add Device Installation Record"
			open={open}
			onCancel={onClose}
			onOk={handleOk}
			confirmLoading={saving}
			okText="Add"
			width={560}
			destroyOnClose>
			<Form form={form} layout="vertical">
				<Divider orientation="left" plain style={{ fontSize: 12 }}>
					Device
				</Divider>
				<Flex gap={12}>
					<Form.Item
						name="deviceInstalled"
						label="Device Installed"
						valuePropName="checked"
						style={{ flex: 1 }}>
						<Switch />
					</Form.Item>
					<Form.Item
						name="deviceSerial"
						label="Device Serial"
						style={{ flex: 3 }}>
						<Input placeholder="ZRF-DEV-0042" />
					</Form.Item>
				</Flex>

				<Divider orientation="left" plain style={{ fontSize: 12 }}>
					SIM
				</Divider>
				<Flex gap={12}>
					<Form.Item
						name="simInstalled"
						label="SIM Installed"
						valuePropName="checked"
						style={{ flex: 1 }}>
						<Switch />
					</Form.Item>
					<Form.Item
						name="simNumber"
						label="SIM Number"
						style={{ flex: 3 }}>
						<Input placeholder="+998901234567" />
					</Form.Item>
				</Flex>

				<Divider orientation="left" plain style={{ fontSize: 12 }}>
					Installation
				</Divider>
				<Flex gap={12}>
					<Form.Item
						name="installLocation"
						label="Location"
						style={{ flex: 2 }}>
						<Input placeholder="Under panel A2" />
					</Form.Item>
					<Form.Item
						name="mountType"
						label="Mount Type"
						style={{ flex: 1 }}>
						<Select
							placeholder="—"
							options={MOUNT_OPTIONS.map((v) => ({ value: v, label: v }))}
						/>
					</Form.Item>
				</Flex>
				<Flex gap={12}>
					<Form.Item
						name="antennaPosition"
						label="Antenna Position"
						style={{ flex: 2 }}>
						<Input placeholder="Top-right corner" />
					</Form.Item>
					<Form.Item
						name="technician"
						label="Technician"
						style={{ flex: 1 }}>
						<Input placeholder="Name" />
					</Form.Item>
				</Flex>
				<Form.Item name="installDate" label="Install Date">
					<DatePicker style={{ width: '100%' }} />
				</Form.Item>

				<Divider orientation="left" plain style={{ fontSize: 12 }}>
					Risk Assessment
				</Divider>
				<Flex gap={12} wrap="wrap">
					{(
						[
							['vibrationRisk', 'Vibration'],
							['dustRisk', 'Dust'],
							['waterRisk', 'Water'],
							['heatRisk', 'Heat']
						] as [string, string][]
					).map(([name, label]) => (
						<Form.Item
							key={name}
							name={name}
							label={label}
							style={{ flex: 1, minWidth: 100 }}>
					<Select
								placeholder="—"
								allowClear
								options={RISK_OPTIONS.map((v) => ({ value: v, label: `${RISK_EMOJI[v]} ${v}` }))}
							/>
						</Form.Item>
					))}
				</Flex>

				<Form.Item name="notes" label="Notes">
					<Input.TextArea rows={2} />
				</Form.Item>
			</Form>
		</Modal>
	)
}

// ─── Single installation card ─────────────────────────────────────────────────
interface InstallCardProps {
	install: DeviceInstallation
	onDeleted: (id: number) => void
}

const InstallCard: FC<InstallCardProps> = ({ install, onDeleted }) => {
	const { t } = useTranslation()

	const handleDelete = async () => {
		try {
			await deleteDeviceInstallation(install.id)
			onDeleted(install.id)
		} catch {
			message.error(t('Error'))
		}
	}

	return (
		<Flex
			vertical
			gap={10}
			style={{
				backgroundColor: '#fff',
				borderRadius: 12,
				padding: '14px 16px',
				boxShadow: '0 2px 8px rgba(26,48,79,0.08)'
			}}>
			{/* header */}
			<Flex align="center" justify="space-between" gap={8}>
				<Flex align="center" gap={8} wrap="wrap">
					<WifiOutlined
						style={{ color: '#3894EB', fontSize: 16 }}
					/>
					{install.installDate && (
						<Text strong style={{ fontSize: 14 }}>
							{new Date(
								install.installDate
							).toLocaleDateString('ru')}
						</Text>
					)}
					{install.technician && (
						<Tag color="cyan">{install.technician}</Tag>
					)}
					{install.mountType && (
						<Tag>{install.mountType}</Tag>
					)}
				</Flex>
				<Popconfirm
					title="Delete this installation record?"
					onConfirm={handleDelete}
					okText="Delete"
					okButtonProps={{ danger: true }}>
					<Button
						icon={<DeleteOutlined />}
						size="small"
						type="text"
						danger
					/>
				</Popconfirm>
			</Flex>

			{/* device + sim */}
			<Flex gap={24} wrap="wrap">
				<Flex vertical gap={2}>
					<Text type="secondary" style={{ fontSize: 11 }}>
						DEVICE
					</Text>
					{install.deviceInstalled ? (
						<Text
							strong
							style={{
								fontSize: 13,
								fontFamily: 'monospace'
							}}>
							{install.deviceSerial || 'Installed'}
						</Text>
					) : (
						<Text type="secondary" style={{ fontSize: 13 }}>
							Not installed
						</Text>
					)}
				</Flex>
				<Flex vertical gap={2}>
					<Text type="secondary" style={{ fontSize: 11 }}>
						SIM
					</Text>
					{install.simInstalled ? (
						<Text
							strong
							style={{
								fontSize: 13,
								fontFamily: 'monospace'
							}}>
							{install.simNumber || 'Installed'}
						</Text>
					) : (
						<Text type="secondary" style={{ fontSize: 13 }}>
							Not installed
						</Text>
					)}
				</Flex>
				{install.installLocation && (
					<Flex vertical gap={2}>
						<Text type="secondary" style={{ fontSize: 11 }}>
							LOCATION
						</Text>
						<Text style={{ fontSize: 13 }}>
							{install.installLocation}
						</Text>
					</Flex>
				)}
				{install.antennaPosition && (
					<Flex vertical gap={2}>
						<Text type="secondary" style={{ fontSize: 11 }}>
							ANTENNA
						</Text>
						<Text style={{ fontSize: 13 }}>
							{install.antennaPosition}
						</Text>
					</Flex>
				)}
			</Flex>

			{/* risk matrix */}
			<RiskMatrix install={install} />

			{install.notes && (
				<Text
					type="secondary"
					style={{ fontSize: 12, fontStyle: 'italic' }}>
					{install.notes}
				</Text>
			)}
		</Flex>
	)
}

// ─── Main component ───────────────────────────────────────────────────────────
export const CarrierOneDeviceInstallation: FC<{
	carrierId: number
}> = ({ carrierId }) => {
	const { t } = useTranslation()
	const [loading, setLoading] = useState(true)
	const [installs, setInstalls] = useState<DeviceInstallation[]>([])
	const [addOpen, setAddOpen] = useState(false)

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const data = await fetchDeviceInstallations(carrierId)
			setInstalls(data)
		} catch {
			message.error(t('Error'))
		} finally {
			setLoading(false)
		}
	}, [carrierId, t])

	useEffect(() => {
		load()
	}, [load])

	const handleCreated = (d: DeviceInstallation) =>
		setInstalls((prev) => [d, ...prev])

	const handleDeleted = (id: number) =>
		setInstalls((prev) => prev.filter((d) => d.id !== id))

	if (loading) {
		return (
			<Flex justify="center" style={{ paddingTop: 40 }}>
				<Spin />
			</Flex>
		)
	}

	return (
		<>
			<Flex
				vertical
				gap={16}
				style={{ width: '100%', maxWidth: 720 }}>
				<Flex justify="space-between" align="center">
					<Title level={5} style={{ margin: 0 }}>
						IoT Device Installations
					</Title>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => setAddOpen(true)}>
						Add Record
					</Button>
				</Flex>

				{installs.length === 0 ? (
					<Empty description="No installation records" />
				) : (
					<Flex vertical gap={12}>
						{installs.map((d) => (
							<InstallCard
								key={d.id}
								install={d}
								onDeleted={handleDeleted}
							/>
						))}
					</Flex>
				)}
			</Flex>

			<AddInstallModal
				carrierId={carrierId}
				open={addOpen}
				onClose={() => setAddOpen(false)}
				onCreated={handleCreated}
			/>
		</>
	)
}
